import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TimezoneMigrationPreviewLine } from '../../../../features/schedule/scheduleTimezone';

type TimezoneChangeConfirmModalProps = {
  show: boolean;
  oldTimeZone: string;
  newTimeZone: string;
  scheduledMatchCount: number;
  unscheduledMigrationCount: number;
  previewLines: TimezoneMigrationPreviewLine[];
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export const TimezoneChangeConfirmModal = ({
  show,
  oldTimeZone,
  newTimeZone,
  scheduledMatchCount,
  unscheduledMigrationCount,
  previewLines,
  isSubmitting,
  onCancel,
  onConfirm,
}: TimezoneChangeConfirmModalProps) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t(prefix + 'timezoneMigrationTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{t(prefix + 'timezoneMigrationBody')}</p>
        <p className="mb-2">
          <strong>{t(prefix + 'timezoneMigrationFromTo', { oldTimeZone, newTimeZone })}</strong>
        </p>
        <p className="text-muted mb-3">
          {t(prefix + 'timezoneMigrationMatchCount', { count: scheduledMatchCount })}
        </p>
        {unscheduledMigrationCount > 0 && (
          <p className="text-warning mb-3">
            {t(prefix + 'timezoneMigrationUnscheduleWarning', {
              count: unscheduledMigrationCount,
            })}
          </p>
        )}
        {previewLines.length > 0 && (
          <div className="border rounded p-2 bg-light">
            <div className="small text-muted mb-2">{t(prefix + 'timezoneMigrationPreview')}</div>
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>{t(prefix + 'timezoneMigrationPreviewItem')}</th>
                  <th>{t(prefix + 'timezoneMigrationPreviewBefore')}</th>
                  <th>{t(prefix + 'timezoneMigrationPreviewAfter')}</th>
                </tr>
              </thead>
              <tbody>
                {previewLines.map((line) => (
                  <tr key={line.label}>
                    <td>{line.label}</td>
                    <td>{line.before}</td>
                    <td>{line.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={isSubmitting}>
          {t(prefix + 'timezoneMigrationCancel')}
        </Button>
        <Button variant="dark" onClick={onConfirm} disabled={isSubmitting}>
          {t(prefix + 'timezoneMigrationConfirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
